// Endpoints RTK Query da feature auth. Tipos manuais espelhando o contrato com
// o back (ver readme §16 + §22). Quando openapi-typescript regenerar generated.ts,
// migrar pra `paths['/auth/login']['post']['responses']['200']`.
// FIXME: aguardar openapi-typescript real (placeholder até codegen rodar).
import { api } from '@/lib/api';
import { clearSession, setToken } from '@/lib/auth/token-storage';
import {
  startTokenRefreshScheduler,
  stopTokenRefreshScheduler,
} from '@/lib/auth/refresh-scheduler';
import type { Role } from '@/config/constants';
import type { AvatarTone } from '@/components/ui/Avatar';
import { currentUserUpdated, sessionEnded, sessionStarted, type CurrentUser } from './slice';

export type Segment = 'GRAFICA' | 'ECOMMERCE' | 'CLINICA' | 'SERVICOS' | 'RESTAURANTE';
export type TeamSize = 'S' | 'M' | 'L' | 'XL';
export type AiTone = 'FORMAL' | 'CALOROSO' | 'DIRETO';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface SignupRequest {
  name: string;
  email: string;
  password: string;
  company: {
    name: string;
    segment: Segment;
    teamSize: TeamSize;
  };
}

/** O ID token que o botão do Google devolve. Nada mais: quem diz quem é a pessoa é o
 * token verificado no back, não o corpo da requisição. */
export interface GoogleLoginRequest {
  idToken: string;
}

/**
 * Segundo passo de quem entrou com Google sem ter conta: o MESMO token volta, agora
 * com os dados da empresa — que o Google não tem como informar.
 */
export interface GoogleSignupRequest {
  idToken: string;
  company: {
    name: string;
    segment: Segment;
    teamSize: TeamSize;
  };
}

/** Corpo do 404 de `/auth/google` quando o e-mail ainda não tem conta aqui. */
export interface GoogleSignupRequired {
  code: 'GOOGLE_SIGNUP_REQUIRED';
  email: string;
  name: string;
}

export interface SessionResponse {
  accessToken: string;
  user: CurrentUser;
}

/**
 * Resposta do signup. A conta nasce PENDENTE de autorização — nenhuma sessão é
 * emitida. O usuário só entra após liberação manual (login passa a funcionar).
 */
export interface SignupResponse {
  status: 'PENDING';
}

/** Resposta crua de `GET /v1/me` — traz `stores`, não `storeIds`. */
export interface MeResponse extends Omit<CurrentUser, 'storeIds'> {
  stores?: { id: string; name: string; role: Role }[];
}

/**
 * Espelha `OnboardingStatus` do back
 * (chatia-api/src/modules/onboarding/onboarding.service.ts). Os nomes são os do
 * contrato — a versão anterior usava `whatsappPaired`/`toneSelected`, que não
 * existem na resposta e vinham sempre `undefined`.
 */
export interface OnboardingStatus {
  complete: boolean;
  hasStore: boolean;
  /** Número REAL conectado e ativo (Embedded Signup / token manual). */
  hasWhatsappPaired: boolean;
  hasTone: boolean;
  hasTeam: boolean;
}

export interface TeamInviteInput {
  name: string;
  email: string;
  role: Exclude<Role, 'GESTOR'>;
}

/**
 * Espelha `OnboardingService.inviteTeam` do back
 * (chatia-api/src/modules/onboarding/onboarding.service.ts:148-180).
 */
export interface InvitedSummary {
  email: string;
  token: string;
  url: string;
}

export interface SkippedInvite {
  email: string;
  /** Códigos conhecidos: `EMAIL_TAKEN`. */
  reason: string;
}

export interface InviteTeamResponse {
  invited: InvitedSummary[];
  skipped: SkippedInvite[];
}

export interface InviteAcceptRequest {
  token: string;
  name: string;
  password: string;
  avatarTone: AvatarTone;
}

export const authApi = api.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation<SessionResponse, LoginRequest>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          setToken(data.accessToken);
          startTokenRefreshScheduler();
          dispatch(sessionStarted({ user: data.user, accessToken: data.accessToken }));
        } catch {
          // erro propagado pra UI mostrar — não loga aqui.
        }
      },
    }),
    // Mesma sessão do login por senha: o Google só troca quem prova a identidade.
    // 404 GOOGLE_SIGNUP_REQUIRED não é falha a exibir — é o desvio para a tela de
    // empresa, e quem decide isso é a UI (ver authErrorCode).
    googleLogin: build.mutation<SessionResponse, GoogleLoginRequest>({
      query: (body) => ({ url: '/auth/google', method: 'POST', body }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          setToken(data.accessToken);
          startTokenRefreshScheduler();
          dispatch(sessionStarted({ user: data.user, accessToken: data.accessToken }));
        } catch {
          // erro propagado pra UI mostrar — não loga aqui.
        }
      },
    }),
    // Como o signup por senha: nasce PENDENTE, sem sessão.
    googleSignup: build.mutation<SignupResponse, GoogleSignupRequest>({
      query: (body) => ({ url: '/auth/google/signup', method: 'POST', body }),
    }),
    // Signup não inicia sessão: a conta nasce PENDENTE de autorização. Sem
    // setToken/sessionStarted — a UI mostra "aguardando liberação".
    signup: build.mutation<SignupResponse, SignupRequest>({
      query: (body) => ({ url: '/auth/signup', method: 'POST', body }),
    }),
    logout: build.mutation<{ ok: true }, void>({
      query: () => ({ url: '/auth/logout', method: 'POST' }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } finally {
          // Logout explícito é o caso em que a flag DEVE sair: o usuário não quer que
          // este browser retome a sessão sozinho no próximo acesso.
          stopTokenRefreshScheduler();
          clearSession();
          dispatch(sessionEnded());
        }
      },
    }),
    me: build.query<CurrentUser, void>({
      // Back expõe em /v1/me (controller separado em modules/auth/me.controller.ts),
      // não /v1/auth/me — vide spec do readme §22.
      query: () => '/me',
      // O back devolve `stores: [{id,name,role}]`, nunca `storeIds` — e o login
      // (`toSessionUser`) também não manda. Sem este mapa, `selectStoreIds` fica
      // permanentemente vazio e quem depende de uma loja não acha nenhuma.
      transformResponse: (raw: MeResponse): CurrentUser => ({
        ...raw,
        storeIds: (raw.stores ?? []).map((s) => s.id),
      }),
      providesTags: ['User'],
    }),
    onboardingStatus: build.query<OnboardingStatus, void>({
      query: () => '/onboarding/status',
      providesTags: ['User'],
    }),
    // Conexão de WhatsApp NÃO mora aqui: o fluxo real é o Embedded Signup em
    // `features/stores/api.ts` (connectWhatsapp / whatsappManualToken).
    setTone: build.mutation<OnboardingStatus, { tone: AiTone }>({
      query: (body) => ({ url: '/onboarding/tone', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),
    inviteTeam: build.mutation<InviteTeamResponse, { storeId: string; members: TeamInviteInput[] }>(
      {
        query: (body) => ({ url: '/onboarding/team', method: 'POST', body }),
        invalidatesTags: (_r, _e, arg) => [
          { type: 'StoreMember' as const, id: arg.storeId },
          { type: 'StoreInvite' as const, id: arg.storeId },
          { type: 'Store' as const, id: 'LIST' },
          'Leaderboard',
          'User',
        ],
      },
    ),
    completeOnboarding: build.mutation<OnboardingStatus, void>({
      query: () => ({ url: '/onboarding/complete', method: 'POST' }),
      invalidatesTags: ['User'],
    }),
    resetPassword: build.mutation<{ ok: true }, { token: string; password: string }>({
      query: (body) => ({ url: '/auth/password-reset/confirm', method: 'POST', body }),
    }),
    // Perfil do próprio usuário (RF-07/RF-08). O back devolve o `me` atualizado —
    // refletimos no slice na hora (Header/Settings/Dashboard) e invalidamos 'User'.
    updateMe: build.mutation<CurrentUser, { name?: string; avatarTone?: AvatarTone }>({
      query: (body) => ({ url: '/me', method: 'PATCH', body }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            currentUserUpdated({
              name: data.name,
              avatarTone: data.avatarTone,
              avatarUrl: data.avatarUrl,
            }),
          );
        } catch {
          // erro propagado pra UI — não loga aqui.
        }
      },
      invalidatesTags: ['User'],
    }),
    uploadAvatar: build.mutation<CurrentUser, FormData>({
      query: (form) => ({ url: '/me/avatar', method: 'POST', body: form }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            currentUserUpdated({
              name: data.name,
              avatarTone: data.avatarTone,
              avatarUrl: data.avatarUrl,
            }),
          );
        } catch {
          /* erro propagado pra UI */
        }
      },
      invalidatesTags: ['User'],
    }),
    changeMyPassword: build.mutation<{ ok: true }, { newPassword: string }>({
      query: (body) => ({ url: '/me/password', method: 'PATCH', body }),
    }),
    acceptInvite: build.mutation<SessionResponse, InviteAcceptRequest>({
      query: (body) => ({ url: '/auth/invite/accept', method: 'POST', body }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          setToken(data.accessToken);
          startTokenRefreshScheduler();
          dispatch(sessionStarted({ user: data.user, accessToken: data.accessToken }));
        } catch {
          /* noop */
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useLoginMutation,
  useGoogleLoginMutation,
  useGoogleSignupMutation,
  useSignupMutation,
  useLogoutMutation,
  useMeQuery,
  useOnboardingStatusQuery,
  useSetToneMutation,
  useInviteTeamMutation,
  useCompleteOnboardingMutation,
  useAcceptInviteMutation,
  useResetPasswordMutation,
  useUpdateMeMutation,
  useUploadAvatarMutation,
  useChangeMyPasswordMutation,
} = authApi;
